import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts'

interface PriceChartProps {
  data?: Array<{ date: string; value: number }>
}

/**
 * 🔴 This used to INVENT the price history it drew.
 *
 * When `data` was empty — which is always, because `DiscountedAsset.priceHistory`
 * is declared in the types but no client code has ever populated it — the
 * component picked one of eight hardcoded seven-day patterns by
 * `seed % patterns.length`, added a `seed % 10 - 5` offset, and rendered the
 * result as a sparkline beside a REAL discount figure. Every "Top Discounts
 * Today" row therefore carried a fabricated price chart that looked exactly
 * like a reading of the market.
 *
 * There is nothing to draw when there is no history, so it draws nothing. The
 * 60px slot is kept so the surrounding card does not reflow if the field is
 * ever populated.
 */
export const PriceChart = ({ data }: PriceChartProps) => {
  const chartData =
    data && data.length > 0
      ? data.map(item => ({ day: item.date || '', value: item.value }))
      : []

  if (chartData.length === 0) {
    return <div className="h-[60px] w-full" aria-hidden="true" />
  }

  // Calculate domain to show variations clearly
  const values = chartData.map(d => d.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const range = maxValue - minValue
  // Add small padding for better visibility
  const padding = range > 0 ? range * 0.15 : 5
  const domainMin = Math.max(0, minValue - padding)
  const domainMax = maxValue + padding

  // Get stroke color based on theme
  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  const strokeColor = isDarkMode ? '#878787' : '#2d2923'

  return (
    <div className="h-[60px] w-full relative">
      {/* Chart area - 28px height */}
      <div className="absolute left-0 top-0 w-full h-7">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <XAxis hide={true} />
            <YAxis 
              hide={true}
              domain={[domainMin, domainMax]}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={strokeColor}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Horizontal line at y=40px */}
      <div className="absolute left-0 right-0 top-10 h-px bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)]" />
      
      {/* Days of week labels at top: 48px */}
      <div 
        className="absolute left-0 top-12 w-full flex items-center gap-[15px] font-plus-jakarta font-normal text-[11px] leading-3 text-[#878787] text-center"
      >
        {chartData.map((item, index) => (
          <span key={index} className="flex-1">
            {item.day}
          </span>
        ))}
      </div>
    </div>
  )
}
